import React, { useEffect, useRef, useState } from 'react';
import useI18n from '@/hooks/useI18n';
import { formatCount, formatMoney } from '@/utils/numberUtils';
import type { CampaignTopPost } from '@/interfaces/campaignKpis';
import { DownloadIcon, ChevronRightIcon, InfoIcon } from '@/svg';
import ToolTip from '@/components/common/ToolTip';

/**
 * Badge labels by content type. An unrecognised type — including the API's
 * `unknown`, which means the media was never archived so its kind could not be
 * derived — gets no badge rather than a raw string.
 */
const MEDIA_TYPE_KEYS: Record<string, string> = {
  reel: 'common.reel',
  post: 'common.post',
  story: 'common.story',
  video: 'common.video',
};

/**
 * Media lives on S3, and a browser ignores an anchor's `download` attribute on
 * a cross-origin URL — it navigates instead, opening the video in a tab. The
 * API proxies the file back same-origin with `Content-Disposition: attachment`,
 * which is how the submission viewer already downloads posts.
 */
const toDownloadHref = (mediaUrl: string): string =>
  `${process.env.REACT_APP_API_URL}/users/downloadLink?url=${encodeURIComponent(
    mediaUrl,
  )}`;

const RING_RADIUS = 8;
const RING_LENGTH = 2 * Math.PI * RING_RADIUS;

/**
 * Plays a card's video only while it is hovered (or tapped, on touch), and
 * drains a ring as it plays so the viewer can see how much is left.
 */
const useHoverPlayback = () => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const ringRef = useRef<SVGCircleElement>(null);
  const frame = useRef(0);
  const pointerType = useRef('mouse');
  // True once frames are actually playing, not when play() is called, so the
  // cover stays up while the first hover is still buffering.
  const [playing, setPlaying] = useState(false);

  // Drawn every animation frame rather than on `timeupdate` (~4 Hz) so the
  // ring drains smoothly, and written straight to the DOM so it costs no
  // re-renders.
  const draw = () => {
    const video = videoRef.current;
    if (video?.duration && ringRef.current) {
      ringRef.current.style.strokeDashoffset = String(
        (RING_LENGTH * video.currentTime) / video.duration,
      );
    }
    frame.current = requestAnimationFrame(draw);
  };

  const start = () => {
    const video = videoRef.current;
    if (!video) return;
    // Rejects when a quick hover-out pauses it first; nothing to recover.
    video.play().catch(() => undefined);
    cancelAnimationFrame(frame.current);
    frame.current = requestAnimationFrame(draw);
  };

  const stop = () => {
    const video = videoRef.current;
    cancelAnimationFrame(frame.current);
    setPlaying(false);
    if (video) {
      video.pause();
      video.currentTime = 0;
    }
  };

  useEffect(() => () => cancelAnimationFrame(frame.current), []);

  return {
    videoRef,
    ringRef,
    playing,
    onPlaying: () => setPlaying(true),
    tileHandlers: {
      onPointerDown: (e: React.PointerEvent) => {
        pointerType.current = e.pointerType;
      },
      onPointerEnter: (e: React.PointerEvent) => {
        if (e.pointerType === 'mouse') start();
      },
      onPointerLeave: (e: React.PointerEvent) => {
        if (e.pointerType === 'mouse') stop();
      },
      // Touch has no hover, so a tap toggles instead — except on the
      // download button, which must keep doing only its own job.
      onClick: (e: React.MouseEvent) => {
        if (pointerType.current === 'mouse') return;
        if ((e.target as HTMLElement).closest('a, button')) return;
        if (videoRef.current?.paused) start();
        else stop();
      },
    },
  };
};

const PostCard: React.FC<{
  post: CampaignTopPost;
  wideOnly?: boolean;
  /** Keeps a full row's column width so a short row packs to the left. */
  packed?: boolean;
}> = ({ post, wideOnly = false, packed = false }) => {
  const { t } = useI18n();

  const typeKey = MEDIA_TYPE_KEYS[post.mediaType];
  const { videoRef, ringRef, playing, onPlaying, tileHandlers } =
    useHoverPlayback();
  const isVideo = !!post.mediaUrl && post.mediaKind === 'video';
  // Hover plays the short muted preview clip when one was built — a fraction
  // of the full file — and the original otherwise. Download keeps the original.
  const hoverSrc = post.previewUrl ?? post.mediaUrl;
  const videoSrc = post.thumbnailUrl
    ? (hoverSrc ?? undefined)
    : `${hoverSrc}#t=0.1`;

  return (
    <div
      data-testid="top-post-card"
      className={`min-w-0 flex-col items-start gap-3 ${
        packed
          ? // A full row's column width (5, or 6 on 2xl, less the 20px gaps),
            // so the spacing matches and only the right side is left empty.
            'flex-none basis-[calc((100%-80px)/5)] 2xl:basis-[calc((100%-100px)/6)]'
          : 'flex-1'
      } ${wideOnly ? 'hidden 2xl:flex' : 'flex'}`}
    >
      <div
        className="relative w-full max-w-[190px] overflow-hidden rounded-xl border border-[#E5E7EB]"
        {...(isVideo ? tileHandlers : {})}
      >
        <div className="relative aspect-[190/269] w-full bg-gradient-to-br from-[#E9E7F5] to-[#D9D5EC]">
          {/* The stored cover shows at rest and the video downloads nothing
              until hovered. Without a cover, the browser fetches just enough
              to paint the first frame (#t=0.1) as the still instead. */}
          {isVideo && (
            <>
              <video
                ref={videoRef}
                src={videoSrc}
                preload={post.thumbnailUrl ? 'none' : 'metadata'}
                muted
                loop
                playsInline
                onPlaying={onPlaying}
                className="h-full w-full object-cover"
              />
              {post.thumbnailUrl && (
                <img
                  src={post.thumbnailUrl}
                  alt=""
                  className={`absolute inset-0 h-full w-full object-cover transition-opacity duration-300 ${
                    playing ? 'opacity-0' : 'opacity-100'
                  }`}
                />
              )}
              {/* Drains as the video plays: what is left, at a glance. */}
              <svg
                aria-hidden
                viewBox="0 0 24 24"
                className={`pointer-events-none absolute bottom-2 right-2 size-6 -rotate-90 transition-opacity duration-300 ${
                  playing ? 'opacity-100' : 'opacity-0'
                }`}
              >
                <circle cx="12" cy="12" r="11" fill="rgba(0,0,0,0.45)" />
                <circle
                  cx="12"
                  cy="12"
                  r={RING_RADIUS}
                  fill="none"
                  stroke="rgba(255,255,255,0.3)"
                  strokeWidth="2.5"
                />
                <circle
                  ref={ringRef}
                  data-testid="video-progress-ring"
                  cx="12"
                  cy="12"
                  r={RING_RADIUS}
                  fill="none"
                  stroke="#FFFFFF"
                  strokeWidth="2.5"
                  strokeLinecap="round"
                  strokeDasharray={RING_LENGTH}
                  strokeDashoffset={0}
                />
              </svg>
            </>
          )}
          {post.mediaUrl && post.mediaKind === 'image' && (
            <img
              src={post.mediaUrl}
              alt=""
              className="h-full w-full object-cover"
            />
          )}
        </div>
        {typeKey && (
          <span
            data-testid="top-post-media-type"
            className="absolute left-2 top-[9px] rounded-full bg-black/60 px-2 py-0.5 font-header text-xs font-normal text-white"
          >
            {t(typeKey)}
          </span>
        )}
        {/* Always shown — downloading a post to reshare is the panel's point.
            Inert until the API supplies a media URL. */}
        {post.downloadUrl ? (
          <a
            data-testid="top-post-download"
            href={toDownloadHref(post.downloadUrl)}
            download
            aria-label={String(t('common.downloadPost'))}
            className="absolute right-2 top-[7px] flex items-center justify-center rounded-[32px] bg-white p-1"
          >
            <DownloadIcon stroke="#4D494F" />
          </a>
        ) : (
          <button
            type="button"
            data-testid="top-post-download"
            disabled
            aria-label={String(t('common.downloadPost'))}
            className="absolute right-2 top-[7px] flex cursor-not-allowed items-center justify-center rounded-[32px] bg-white p-1"
          >
            <DownloadIcon stroke="#4D494F" />
          </button>
        )}
      </div>

      <div className="flex w-full flex-col gap-0.5">
        <p
          data-testid="top-post-creator"
          className="w-full truncate font-header text-sm font-semibold text-[#1F2024]"
        >
          {post.creatorHandle}
        </p>
        {/* Hidden rather than "— followers" when no count was ever captured. */}
        {post.followers !== null && (
          <p
            data-testid="top-post-followers"
            className="w-full font-header text-xs font-normal text-[#9B919D]"
          >
            {t('common.followersCount', { count: formatCount(post.followers) })}
          </p>
        )}
      </div>

      <div className="flex w-full items-center gap-2">
        <p
          data-testid="top-post-rewarded"
          className="whitespace-nowrap font-header text-xs"
        >
          <span className="font-semibold text-[#1F2024]">
            {formatMoney(post.rewarded, { cents: true })}
          </span>{' '}
          <span className="font-normal text-[#9B919D]">
            {t('common.rewarded')}
          </span>
        </p>
        <span className="size-[3px] shrink-0 rounded-full bg-[#6B7280]/50" />
        <p
          data-testid="top-post-views"
          className="whitespace-nowrap font-header text-xs text-[#6B7280]"
        >
          <span className="font-semibold">{formatCount(post.views)}</span>{' '}
          <span className="font-normal">{t('common.viewsLabel')}</span>
        </p>
      </div>
    </div>
  );
};

/**
 * Cards shown inline; the rest sit behind "View all". Figma's row holds five —
 * a sixth only fits without squeezing the metrics line from 1536px (2xl) up.
 */
const MAX_VISIBLE_POSTS = 6;
const ALWAYS_VISIBLE_POSTS = 5;
// Up to this many posts sit left-aligned instead of stretching across the row.
const PACKED_MAX_POSTS = 4;

interface TopPostsPanelProps {
  posts: CampaignTopPost[];
  /** Total posts on the campaign, when more exist than are shown. */
  totalCount?: number;
  onViewAll?: () => void;
}

/**
 * Best-performing posts for the campaign, ranked by views. The API already
 * ranks and caps them; this hides itself entirely when there is nothing to show
 * rather than rendering an empty card.
 */
const TopPostsPanel: React.FC<TopPostsPanelProps> = ({
  posts,
  totalCount,
  onViewAll,
}) => {
  const { t } = useI18n();

  if (posts.length === 0) return null;

  const visible = posts.slice(0, MAX_VISIBLE_POSTS);
  const total = totalCount ?? posts.length;
  // Against the five every screen shows, so a narrow screen hiding the sixth
  // still offers the way to it.
  const showViewAll = !!onViewAll && total > ALWAYS_VISIBLE_POSTS;

  return (
    <div data-testid="top-posts-panel" className="w-full">
      <div className="flex w-full flex-col gap-6 rounded-3xl border border-[#E5E7EB] bg-white px-6 py-8">
        <div className="flex w-full flex-col items-start justify-between gap-4 sm:flex-row sm:items-center">
          <div className="flex flex-col items-start">
            <div className="flex items-center gap-1">
              <p className="font-header text-base font-semibold leading-6 tracking-[0.096px] text-[#172B4D]">
                {t('common.topPosts')}
              </p>
              <ToolTip
                text={String(t('common.topPostsTooltip'))}
                position="top"
                className="tooltip-nowrap"
              >
                <InfoIcon
                  data-testid="top-posts-info"
                  className="size-4 cursor-pointer text-[#172B4D]"
                />
              </ToolTip>
            </div>
            <p className="font-header text-xs font-normal leading-4 text-[#9B919D]">
              {t('common.topPostsSubtitle')}
            </p>
          </div>
          {showViewAll && (
            <button
              type="button"
              data-testid="top-posts-view-all"
              onClick={onViewAll}
              className="flex shrink-0 items-center justify-center gap-2 rounded-full bg-[#F9F9F9] px-8 py-3 font-header text-sm font-bold tracking-[0.21px] text-[#231254] transition-colors hover:bg-[#EFEFEF] max-sm:w-full"
            >
              {t('common.viewAllCount', { count: total })}
              <ChevronRightIcon stroke="#231254" />
            </button>
          )}
        </div>
        <div className="flex w-full flex-wrap items-start gap-5 md:flex-nowrap">
          {visible.map((post, index) => (
            <PostCard
              key={post.id}
              post={post}
              wideOnly={index >= ALWAYS_VISIBLE_POSTS}
              packed={visible.length <= PACKED_MAX_POSTS}
            />
          ))}
        </div>
      </div>
    </div>
  );
};

export default TopPostsPanel;
